import { Button, Menu, MenuItem, Popover, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import * as XLSX from "xlsx";
import React, { useState } from "react";

import { AdminViewApi, IMaintainedProjectDetails } from "../../api/AdminViewApi";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import UUID from "../../utils/UUID";
import { writeLocalStorage } from "../../utils/writeLocalStorage";
import styles from "./AdminViewSection.module.scss";
import inputStyles from "./ProjectAnalysisSection.module.scss";

const getStatusLabel = (inactive?: string): string => {
  if (inactive === "X") {
    return "Inactive";
  }

  return inactive === "" || inactive === "Active" ? "Active" : "Not marked inactive";
};

export const ProjectAnalysisSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const [projects, setProjects] = useState("");
  const [analysisResults, setAnalysisResults] = useState<ILicenseLogEntry[]>([]);
  const [maintainedDetails, setMaintainedDetails] = useState<IMaintainedProjectDetails[]>([]);
  const [showingMaintainedDetails, setShowingMaintainedDetails] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSkippedProjects, setIsLoadingSkippedProjects] = useState(false);
  const [isLoadingSkipReasons, setIsLoadingSkipReasons] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const filterByProjectId = (
    entries: ILicenseLogEntry[],
    projectId: string
  ): ILicenseLogEntry[] => {
    if (!projectId) {
      return entries;
    }

    return entries.filter((entry) => {
      const entryProjectId =
        entry.PROJECT_ID ?? entry.project_id ?? entry.PROJECT_ID_SKIP;
      return String(entryProjectId ?? "").trim() === projectId;
    });
  };

  const handleShowProducts = (products: ILicenseLogEntry[]) => {
    const storageKey = `swpo.project-analysis.products.${UUID.randomUUID()}`;
    writeLocalStorage(storageKey, products);
    const productsUrl = `${window.location.origin}/admin-view/project-analysis/products?key=${encodeURIComponent(
      storageKey
    )}`;
    window.open(productsUrl, "_blank", "noopener,noreferrer");
  };

  const handleSkippedProjects = async () => {
    setIsLoadingSkippedProjects(true);
    setIsLoading(true);
    await request.send(async () => {
      const projectId = projects.trim();
      const skipped = await AdminViewApi.syncSkippedProjects(projectId);
      setAnalysisResults(filterByProjectId(skipped, projectId));
      setShowingMaintainedDetails(false);
      setHasLoaded(true);
    });
    setIsLoadingSkippedProjects(false);
    setIsLoading(false);
  };

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = analysisResults) => {
    if (rows.length === 0) return;

    const columns = Object.keys(rows[0] ?? {});
    const escapeCell = (value: unknown): string => {
      const text = value == null ? "" : String(value).replace(/\r?\n/g, " ");
      return `"${text.replace(/"/g, '""')}"`;
    };

    if (format === "csv") {
      const csv = [
        columns.join(","),
        ...rows.map((row) => columns.map((column) => escapeCell((row as Record<string, unknown>)[column])).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `project_analysis.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData = rows.map((row) => {
      const values: Record<string, unknown> = {};
      columns.forEach((column) => {
        values[column] = (row as Record<string, unknown>)[column] ?? "";
      });
      return values;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: columns });
    XLSX.utils.book_append_sheet(workbook, worksheet, "ProjectAnalysis");

    const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `project_analysis.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() =>
          exportData(
            "csv",
            table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : analysisResults
          )
        }
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() =>
          exportData(
            "excel",
            table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : analysisResults
          )
        }
      />
    </Menu>
  );

  const handleSkipReasons = async () => {
    setIsLoadingSkipReasons(true);
    setIsLoading(true);
    await request.send(async () => {
      const projectId = projects.trim();
      const reasons = await AdminViewApi.getSkipReasons(projectId);
      setMaintainedDetails(reasons);
      setShowingMaintainedDetails(true);
      setHasLoaded(true);
    });
    setIsLoadingSkipReasons(false);
    setIsLoading(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.controls} ${inputStyles.controls}`}>
        <label className={inputStyles.label}>Project ID:</label>
        <input
          className={inputStyles.input}
          value={projects}
          onChange={(e) => setProjects(e.target.value)}
          placeholder="Enter project(s)"
        />
        <Button
          className={styles.confirmButton}
          intent="primary"
          text={t(texts.adminView.showSkippedProjects)}
          onClick={handleSkippedProjects}
          loading={isLoadingSkippedProjects}
        />
        <Button
          className={styles.confirmButton}
          intent="primary"
          text={t(texts.adminView.showMaintainedDetails)}
          onClick={handleSkipReasons}
          loading={isLoadingSkipReasons}
          disabled={!projects.trim()}
        />
        {hasLoaded && !showingMaintainedDetails && (
          <div className={styles.countHeader}>
            {t(texts.adminView.recordsFound, { count: visibleCount })}
          </div>
        )}
        {hasLoaded && showingMaintainedDetails && (
          <div className={styles.countHeader}>
            {t(texts.adminView.projectsFound, { count: maintainedDetails.length })}
          </div>
        )}
      </div>

      {hasLoaded && showingMaintainedDetails && (
        <div className={inputStyles.maintainedDetails}>
          {maintainedDetails.length > 0 ? (
            maintainedDetails.map((project, projectIndex) => (
              (() => {
                const inactiveStatuses = project.INACT_STAT ?? [];
                const projectStatuses = project.PRART_STUFE ?? [];
                const hasInactiveFlag = [...inactiveStatuses, ...projectStatuses].some(
                  (status) => status.INACT === "X"
                );
                const legacyProductList = Array.isArray(project.CHECK_PROD)
                  ? ""
                  : project.CHECK_PROD?.PRODUCTS ?? "";
                const productIds = legacyProductList
                  .toString()
                  .split(",")
                  .map((productId) => productId.trim())
                  .filter(Boolean);
                const checkedProducts = Array.isArray(project.CHECK_PROD)
                  ? project.CHECK_PROD
                  : productIds.map((productId) => ({ PRODUCTS_ID: productId, LIC_INFO: [] }));
                const returnedCustomers = project.BUPA_CUST
                  ? Array.isArray(project.BUPA_CUST)
                    ? project.BUPA_CUST
                    : [project.BUPA_CUST]
                  : [];
                const customers = Array.from(
                  new Map(
                    returnedCustomers.map((customer) => [
                      [
                        customer.BUPA_AND_CUST_ID,
                        customer.CUSTOMER_ID,
                        customer.CUSTOMER_NAME,
                      ].join("|"),
                      customer,
                    ])
                  ).values()
                );

                return (
                  <article
                    className={inputStyles.projectDetails}
                    key={`${project.PROJECT_ID ?? "project"}-${projectIndex}`}
                  >
                    <header className={inputStyles.projectHeader}>
                      <div>
                        <span className={inputStyles.projectEyebrow}>
                          {t(texts.adminView.project)}
                        </span>
                        <h2 className={inputStyles.projectHeading}>
                          {project.PROJECT_ID || `#${projectIndex + 1}`}
                        </h2>
                      </div>
                      <span
                        className={`${inputStyles.healthBadge} ${
                          hasInactiveFlag ? inputStyles.healthInactive : inputStyles.healthClear
                        }`}
                      >
                        <span className={inputStyles.healthDot} aria-hidden="true" />
                        {hasInactiveFlag
                          ? t(texts.adminView.markedInactive)
                          : t(texts.adminView.noInactiveFlags)}
                      </span>
                    </header>

                    <div className={inputStyles.detailSections}>
                      <section className={inputStyles.detailSection}>
                        <h3>{t(texts.adminView.overallStatus)}</h3>
                        <div className={inputStyles.detailContent}>
                          {inactiveStatuses.length ? (
                            <ul className={inputStyles.statusList}>
                              {inactiveStatuses.map((status, index) => (
                                <li key={`${status.STAT ?? "status"}-${index}`}>
                                  <span className={inputStyles.statusLabel}>
                                    {t(texts.adminView.projectStatus)}
                                  </span>
                                  <span
                                    className={`${inputStyles.statusValue} ${
                                      getStatusLabel(status.INACT) === "Active"
                                        ? inputStyles.statusValueActive
                                        : inputStyles.statusValueInactive
                                    }`}
                                  >
                                    {getStatusLabel(status.INACT)}
                                  </span>
                                  <span className={inputStyles.statusCode}>
                                    Code {status.STAT || t(texts.adminView.notProvided)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className={inputStyles.noDetail}>
                              {t(texts.adminView.noOverallStatusReturned)}
                            </p>
                          )}
                        </div>
                      </section>

                      <section className={inputStyles.detailSection}>
                        <h3>Project type status</h3>
                        <div className={inputStyles.detailContent}>
                          {projectStatuses.length ? (
                            <ul className={inputStyles.statusList}>
                              {projectStatuses.map((status, index) => (
                                <li key={`${status.PRART ?? "type"}-${status.STAT ?? "status"}-${index}`}>
                                  <span className={inputStyles.statusLabel}>
                                    {status.PRART ||
                                      `Type ${t(texts.adminView.notProvided)}`} · Level {status.STUFE ?? "—"}
                                  </span>
                                  <span
                                    className={`${inputStyles.statusValue} ${
                                      getStatusLabel(status.INACT) === "Active"
                                        ? inputStyles.statusValueActive
                                        : inputStyles.statusValueInactive
                                    }`}
                                  >
                                    {getStatusLabel(status.INACT)}
                                  </span>
                                  <span className={inputStyles.statusCode}>
                                    Code {status.STAT || t(texts.adminView.notProvided)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className={inputStyles.noDetail}>
                              {t(texts.adminView.noProjectTypeStatusesReturned)}
                            </p>
                          )}
                        </div>
                      </section>

                      <section className={inputStyles.detailSection}>
                        <h3>Products checked</h3>
                        <div className={inputStyles.detailContent}>
                          {checkedProducts.length ? (
                            <div className={inputStyles.productTags}>
                              {checkedProducts.map((product, index) => (
                                <Popover
                                  key={`${product.PRODUCTS_ID ?? "product"}-${index}`}
                                  placement="bottom-start"
                                  content={
                                    <div className={inputStyles.productInfoPopover}>
                                      <h4>Product {product.PRODUCTS_ID || "details"}</h4>
                                      {product.LIC_INFO?.length ? (
                                        <dl className={inputStyles.productInfoList}>
                                          {product.LIC_INFO.map((info, infoIndex) => (
                                            <React.Fragment
                                              key={`${info.NAME ?? "attribute"}-${infoIndex}`}
                                            >
                                              <dt>{info.NAME || `Attribute ${infoIndex + 1}`}</dt>
                                              <dd>
                                                {info.VALUE === null || info.VALUE === undefined || info.VALUE === ""
                                                  ? t(texts.adminView.notProvided)
                                                  : typeof info.VALUE === "object"
                                                    ? JSON.stringify(info.VALUE)
                                                    : String(info.VALUE)}
                                              </dd>
                                            </React.Fragment>
                                          ))}
                                        </dl>
                                      ) : (
                                        <p className={inputStyles.noDetail}>
                                          {t(texts.adminView.noLicenseInformationAvailable)}
                                        </p>
                                      )}
                                    </div>
                                  }
                                >
                                  <Button
                                    className={inputStyles.productTag}
                                    intent="primary"
                                    text={product.PRODUCTS_ID || `Product ${index + 1}`}
                                  />
                                </Popover>
                              ))}
                            </div>
                          ) : (
                            <p className={inputStyles.noDetail}>
                              {t(texts.adminView.noProductIdsReturned)}
                            </p>
                          )}
                        </div>
                      </section>

                      <section className={inputStyles.detailSection}>
                        <h3>Bupa customer details</h3>
                        <div className={inputStyles.detailContent}>
                          {customers.length ? (
                            <ul className={inputStyles.customerList}>
                              {customers.map((customer, index) => (
                                <li
                                  className={inputStyles.customerItem}
                                  key={`${customer.CUSTOMER_ID ?? "customer"}-${index}`}
                                >
                                  <p className={inputStyles.customerName}>
                                    {customer.CUSTOMER_NAME ||
                                      t(texts.adminView.customerNameNotProvided)}
                                  </p>
                                  <dl className={inputStyles.customerIds}>
                                    <div>
                                      <dt>Customer ID</dt>
                                      <dd>
                                        {customer.CUSTOMER_ID || t(texts.adminView.notProvided)}
                                      </dd>
                                    </div>
                                    <div>
                                      <dt>Business partner ID</dt>
                                      <dd>
                                        {customer.BUPA_AND_CUST_ID ||
                                          t(texts.adminView.notProvided)}
                                      </dd>
                                    </div>
                                  </dl>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className={inputStyles.noDetail}>
                              {t(texts.adminView.noBupaCustomerDetailsReturned)}
                            </p>
                          )}
                        </div>
                      </section>
                    </div>
                  </article>
                );
              })()
            ))
          ) : (
            <div className={inputStyles.noMaintainedDetails}>
              {t(texts.adminView.noMaintainedDetailsFound)}
            </div>
          )}
        </div>
      )}

      {hasLoaded && !showingMaintainedDetails && (
        <div className={inputStyles.tableContainer}>
          <Table2
            data={analysisResults}
            isLoading={isLoading}
            noResultsTitle={t(texts.adminView.noReleaseData)}
            onVisibleCountChange={(count) => setVisibleCount(count)}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            renderCell={(columnId, value) => {
              const columnName = String(columnId).toUpperCase();
              if (columnName !== "PRODUCTS") {
                return undefined;
              }

              const products = Array.isArray(value)
                ? (value as ILicenseLogEntry[])
                : typeof value === "string"
                  ? (() => {
                      try {
                        const parsed = JSON.parse(value);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch {
                        return [];
                      }
                    })()
                  : [];

              return (
                <Button
                  intent="primary"
                  text="Show Products"
                  onClick={() => handleShowProducts(products)}
                  disabled={products.length === 0}
                />
              );
            }}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectAnalysisSection;
